import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Moon, Sun, Sunrise, Sunset,
  Snowflake, Wind, BedDouble, Armchair,
  Wifi, Zap, Shield, MapPin, Navigation,
  SwitchCamera, Search, ChevronDown,
  ChevronUp, SlidersHorizontal, Trophy, Crown,
  Wallet, Satellite, BatteryCharging, Layers,
  Droplets, Cookie, Tv2, AlignJustify, Calendar,
  ChevronsRight, BarChart3, AlertTriangle, Bus, ArrowRight, Image,
  FileText, MapPinned,
  Star, Info
} from "lucide-react";
import BusCompare, { BusCompareTray } from "./BusCompare/BusCompare.jsx";
import { createBusBookingPayload } from "./Busbooking/Busbooking.jsx";
import { saveBusBooking } from "./Busbooking/Busbooking.js";
import "./Bus.css";
import { useCities } from "../../../services/usecitiesbus";
import { searchBuses, repriceBus, fetchSeatLayout } from "../../../services/busApi";
import { adaptApiBus } from "../../../services/busAdapt";
import usePageMeta from "../../../hooks/usePageMeta";

const BUS_PRICING_API_BASE =
  (process.env.REACT_APP_API_URL || "https://makebeetrip.com")
    .replace(/\/+$/, "") + "/api";

  async function getBusAdminPrice({
  supplierFareTotal,
  supplierTaxTotal = 0,
  passengerCount = 1,
  seatCount = 1,
  busId,
  travelDate,
}) {
  const response = await fetch(
    `${BUS_PRICING_API_BASE}/bus-bookings/pricing-preview`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        supplierFareTotal: Number(supplierFareTotal || 0),
        supplierTaxTotal: Number(supplierTaxTotal || 0),
        passengerCount: Number(passengerCount || 1),
        seatCount: Number(seatCount || 1),
        busId: String(busId ?? ""),
        travelDate,
      }),
    }
  );

  const data = await response.json();

  if (!response.ok || !Number.isFinite(Number(data.payableTotal))) {
    throw new Error(data.error || "Could not calculate bus admin price");
  }

  return data;
}  

// ── Helpers ────────────────────────────────────────────────────────────────
function StarRating({ rating }) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <span className="star-rating" aria-label={`${rating} stars`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={s <= full ? "star filled" : s === full + 1 && half ? "star half" : "star"}>★</span>
      ))}
      <span className="rating-num">{rating.toFixed(1)}</span>
    </span>
  );
}

function RatingBadge({ rating }) {
  const color = rating >= 4.5 ? "badge-green" : rating >= 3.5 ? "badge-amber" : "badge-red";
  return <span className={`rating-badge ${color}`}>★ {rating.toFixed(1)}</span>;
}

function AmenityChips({ amenities }) {
  return (
    <div className="amenity-chips">
      {amenities.map((a) =>
        amenityIcons[a] ? (
          <span key={a} className="chip" title={amenityIcons[a].label}>
            {amenityIcons[a].icon} <span>{amenityIcons[a].label}</span>
          </span>
        ) : null
      )}
    </div>
  );
}

// ── City Autocomplete Input ────────────────────────────────────────────────
function CityAutocomplete({ value, onChange, placeholder, allCities, label, icon }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const wrapRef = useRef(null);

  // Sync external value changes
  useEffect(() => { setQuery(value); }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

 
const filtered = useMemo(() => {
  const q = query.trim().toLowerCase();

  if (!q) return allCities.slice(0, 8);

  return allCities
    .filter((city) =>
      city.toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const aName = a.trim().toLowerCase();
      const bName = b.trim().toLowerCase();

      // Show the exact city match first.
      if (aName === q && bName !== q) return -1;
      if (bName === q && aName !== q) return 1;

      // Then show cities beginning with the query.
      const aStarts = aName.startsWith(q);
      const bStarts = bName.startsWith(q);

      if (aStarts && !bStarts) return -1;
      if (bStarts && !aStarts) return 1;

      return aName.localeCompare(bName);
    })
    .slice(0, 10);
}, [query, allCities]);


  const handleSelect = (city) => {
    setQuery(city);
    onChange(city);
    setOpen(false);
  };

  return (
    <div className="bp-search-field city-autocomplete-wrap" ref={wrapRef} style={{ position: "relative" }}>
      <div className="field-icon-box">{icon}</div>
      <span className="field-tag">{label}</span>
      <input
  value={query}
  onChange={(e) => {
    setQuery(e.target.value);
    onChange(e.target.value);
    setOpen(true);
  }}
  onFocus={() => {
    setQuery("");
    onChange("");
    setOpen(true);
  }}
  placeholder={placeholder}
  autoComplete="off"
/>
      {open && filtered.length > 0 && (
        <ul className="city-dropdown">
          {filtered.map((city) => (
            <li key={city} onMouseDown={() => handleSelect(city)}>
              <MapPin size={13} style={{ color: 'var(--em)', flexShrink: 0 }} />
              {city}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Departure slot helper (fixes filter) ──────────────────────────────────
function getDepartureSlot(timeStr) {
  if (!timeStr) return "other";
  // Handle both "HH:MM" and full datetime strings
  const timePart = timeStr.includes("T") ? timeStr.split("T")[1] : timeStr;
  const [hStr] = timePart.split(":");
  const h = parseInt(hStr, 10);
  if (isNaN(h)) return "other";
  if (h < 6) return "before6";
  if (h < 12) return "6to12";
  if (h < 18) return "12to18";
  return "after18";
}

function CheckboxGroup({ label, options, selected, onChange }) {
  const toggle = (val) =>
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  return (
    <div className="filter-group">
      <h4 className="filter-label">{label}</h4>
      <div className="checkbox-list">
        {options.map(({ value, label: lbl }) => (
          <label key={value} className="checkbox-item">
            <input
              type="checkbox"
              checked={selected.includes(value)}
              onChange={() => toggle(value)}
            />
            <span className="custom-check" />
            {lbl}
          </label>
        ))}
      </div>
    </div>
  );
}

function DetailEmpty({ children }) {
  return <div className="detail-empty">{children}</div>;
}

function PointRows({ points, dotClass, fallbackTime }) {
  if (!points.length) return <DetailEmpty>No points returned by the API.</DetailEmpty>;

  return points.map((point, index) => {
    const name = point.name || point.cityPointName || point.location || point.address || "Point";
    const meta = [point.time || fallbackTime, point.address || point.location, point.landmark]
      .filter(Boolean)
      .join(" - ");

    return (
      <div key={`${name}-${point.id || point.bpId || index}`} className="point-row">
        <span className={`p-dot ${dotClass}`} />
        <div>
          <div className="p-name">{name}</div>
          {meta && <div className="p-time">{meta}</div>}
        </div>
      </div>
    );
  });
}

// Weekly date strip — generated fresh each render cycle
function getDateStrip() {
  // Use local date, not UTC (fixes IST timezone issue)

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);

    // Format as YYYY-MM-DD using local time, not UTC
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    return {
      dateObj: d,
      day: d.toLocaleDateString("en-IN", { weekday: "short" }),
      date: d.getDate(),
      month: d.toLocaleDateString("en-IN", { month: "short" }),
      label: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
      iso,
    };
  });
}
function getLocalToday() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

function formatBusDate(iso) {
  if (!iso) return { day: "--", month: "----", weekday: "Select date" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: "--", month: "----", weekday: "Invalid" };
  return {
    day: d.toLocaleDateString("en-IN", { day: "2-digit" }),
    month: d.toLocaleDateString("en-IN", { month: "short" }),
    weekday: d.toLocaleDateString("en-IN", { weekday: "short" }),
  };
}

function getMonthGrid(monthDate) {
  const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const startDay = firstOfMonth.getDay();
  const startDate = new Date(firstOfMonth);
  startDate.setDate(firstOfMonth.getDate() - startDay);

  return Array.from({ length: 42 }, (_, index) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + index);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return {
      dateObj: d,
      iso,
      day: d.getDate(),
      month: d.getMonth(),
      weekday: d.toLocaleDateString("en-IN", { weekday: "short" }),
      inMonth: d.getMonth() === monthDate.getMonth(),
    };
  });
}

function getMonthLabel(monthDate) {
  return monthDate.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

const amenityIcons = {
  wifi: { icon: <Wifi size={11} />, label: "WiFi" },
  charging: { icon: <BatteryCharging size={11} />, label: "Charging" },
  blanket: { icon: <Layers size={11} />, label: "Blanket" },
  water: { icon: <Droplets size={11} />, label: "Water Bottle" },
  snacks: { icon: <Cookie size={11} />, label: "Snacks" },
  movie: { icon: <Tv2 size={11} />, label: "Entertainment" },
  pillow: { icon: <AlignJustify size={11} />, label: "Pillow" },
};
const sortOptions = [
  { value: "relevance", label: "Relevance" },
  { value: "price-asc", label: "Price ↑" },
  { value: "price-desc", label: "Price ↓" },
  { value: "rating", label: "Rating" },
  { value: "fastest", label: "Fastest" },
  { value: "departure", label: "Departure" },
  { value: "arrival", label: "Arrival" },
];

const makeSeatDeck = (bus, deck) => {
  const prefixes = deck === "lower" ? ["L", "D"] : ["U", "DU"];
  const basePrices = [bus.price, Math.max(500, bus.price - 120), bus.price + 180, bus.price + 300];
  return Array.from({ length: 18 }, (_, index) => {
    const row = Math.floor(index / 3) + 1;
    const col = index % 3;
    const sleeper = col === 2;
    const code = `${sleeper ? prefixes[1] : prefixes[0]}${row}${col === 1 ? "B" : ""}`;
    const statusSeed = (bus.id * 7 + index * 3 + (deck === "upper" ? 5 : 0)) % 11;
    return {
      code,
      price: basePrices[(index + bus.id) % basePrices.length],
      ladies: statusSeed === 2,
      booked: statusSeed === 0 || statusSeed === 7,
    };
  });
};

function SeatDeck({ title, priceFrom, seats, selectedSeats, onToggle }) {
  return (
    <div className="seat-deck">
      <div className="seat-deck-title">
        <span>{title}</span>
        {priceFrom != null && (
          <span className="seat-deck-price">From ₹{priceFrom.toLocaleString()}</span>
        )}
      </div>
      <div className="seat-grid">
        {seats.map((seat) => {
          const selected = selectedSeats.some((s) => s.code === seat.code);
          return (
            <div
              key={seat.code}
              role="button"
              tabIndex={seat.booked ? -1 : 0}
              className={[
                "seat-cell",
                seat.booked ? "booked" : "",
                seat.ladies ? "ladies" : "",
                selected ? "selected" : "",
              ].filter(Boolean).join(" ")}
              aria-disabled={seat.booked}
              onClick={() => { if (!seat.booked) onToggle(seat); }}
              onKeyDown={(e) => {
                if (!seat.booked && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault(); onToggle(seat);
                }
              }}
            >
              <strong>{seat.code}</strong>

<small className="seat-type-text">
  {seat.seatType}
</small>

<span>₹{seat.price.toLocaleString()}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BusInfoPanel({ bus }) {
  const amenities = bus.amenities || [];
  const policies = bus.policies || [];

  return (
    <div className="wizard-panel">
      <div className="info-block">
        <h4>Trip Overview</h4>
        <div className="info-grid">
          <div><span>Operator</span><strong>{bus.operator}</strong></div>
          <div><span>Bus Type</span><strong>{bus.busType || "—"}</strong></div>
          <div>
  <span>Seat Type</span>
  <strong>
    {bus.seater && bus.sleeper
      ? "Seater / Sleeper"
      : bus.sleeper
        ? "Sleeper"
        : "Seater"}
  </strong>
</div>
          <div><span>AC / Non-AC</span><strong>{bus.isAC ? "AC" : "Non-AC"}</strong></div>
          <div><span>Duration</span><strong>{bus.duration || "—"}</strong></div>
        </div>
      </div>

      {amenities.length > 0 && (
        <div className="info-block">
          <h4>Amenities</h4>
          <div className="info-amenities">
            {amenities.map((a) =>
              amenityIcons[a] ? (
                <span key={a} className="chip">{amenityIcons[a].icon} {amenityIcons[a].label}</span>
              ) : null
            )}
          </div>
        </div>
      )}

      <div className="info-block">
        <h4>Policies</h4>
        {policies.length ? (
          <ul className="info-policy-list">
            {policies.map((p, i) => (
              <li key={`${p.title}-${i}`}>
                <strong>{p.title}</strong>
                {p.text && <span>{p.text}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="info-empty">
            Cancellation and reschedule charges apply as per operator policy — check fare details before booking.
          </p>
        )}
      </div>
    </div>
  );
}

function SeatLegendPanel() {
  return (
    <div className="wizard-panel">
      <div className="info-block">
        <h4>How Seat Selection Works</h4>
        <p className="info-empty">
          Tap any available seat on the next screen to select it. Prices vary by seat position and deck —
          you can select more than one seat for group travel.
        </p>
      </div>
      <div className="info-block">
        <h4>Seat Legend</h4>
        <ul className="info-legend-list">
          <li><i className="ladies" /><div><strong>Ladies</strong><span>Reserved for solo women travellers</span></div></li>
          <li><i className="selected" /><div><strong>Selected</strong><span>Seats you've currently chosen</span></div></li>
          <li><i /><div><strong>Available</strong><span>Open for booking</span></div></li>
          <li><i className="booked" /><div><strong>Booked</strong><span>Already taken by another traveller</span></div></li>
        </ul>
      </div>
    </div>
  );
}

function BusSeatModal({ bus, from, to, date, searchTrackId, onClose }) {
  const navigate = useNavigate();
  const [repriceTotal, setRepriceTotal] = useState(null);
const [adminPreviewTotal, setAdminPreviewTotal] = useState(null);
const [adminPricingLoading, setAdminPricingLoading] = useState(false);
const [selectedSeats, setSelectedSeats] = useState([]);
  const [seatLayout, setSeatLayout] = useState({ lower: [], upper: [] });
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [layoutError, setLayoutError] = useState(null);
  const [pointTab, setPointTab] = useState("boarding");

  const [boardingPoints, setBoardingPoints] = useState([]);
  const [droppingPoints, setDroppingPoints] = useState([]);
  const [boarding, setBoarding] = useState(null);
  const [dropping, setDropping] = useState(null);

  // ── Wizard step state ────────────────────────────────────────
  const steps = [
    { key: "busInfo", label: "Bus Information" },
    { key: "seatInfo", label: "Seat Information" },
    { key: "seats", label: "Seat Selection" },
  ];
  const stepOrder = steps.map((s) => s.key);
  const [activeStep, setActiveStep] = useState("busInfo");
  const [maxStepReached, setMaxStepReached] = useState(0);
  const stepIndex = stepOrder.indexOf(activeStep);

  const goNext = () => {
    const nextIdx = Math.min(stepIndex + 1, stepOrder.length - 1);
    setActiveStep(stepOrder[nextIdx]);
    setMaxStepReached((m) => Math.max(m, nextIdx));
  };
  const goBack = () => setActiveStep(stepOrder[Math.max(stepIndex - 1, 0)]);
  const goToStep = (key) => {
    if (stepOrder.indexOf(key) <= maxStepReached) setActiveStep(key);
  };

  const lowerSeats = seatLayout.lower;
  const upperSeats = seatLayout.upper;

  const priceOptions = useMemo(
    () => ["All", ...Array.from(new Set([...lowerSeats, ...upperSeats].map((s) => s.price)))
      .sort((a, b) => a - b).slice(0, 4)],
    [lowerSeats, upperSeats]
  );
  const [priceFilter, setPriceFilter] = useState("All");

  const visibleLower = priceFilter === "All" ? lowerSeats : lowerSeats.filter((s) => s.price === priceFilter);
  const visibleUpper = priceFilter === "All" ? upperSeats : upperSeats.filter((s) => s.price === priceFilter);

  const lowerMinPrice = visibleLower.length ? Math.min(...visibleLower.map((s) => s.price)) : null;
  const upperMinPrice = visibleUpper.length ? Math.min(...visibleUpper.map((s) => s.price)) : null;

  const total = selectedSeats.reduce((sum, seat) => sum + seat.price, 0);

  useEffect(() => {
  let cancelled = false;

  async function updateAdminPreview() {
    if (!selectedSeats.length) {
      setAdminPreviewTotal(null);
      setRepriceTotal(null);
      setAdminPricingLoading(false);
      return;
    }

    setAdminPricingLoading(true);

    try {
      const supplierFareTotal = selectedSeats.reduce(
        (sum, seat) => sum + Number(seat.price || 0),
        0
      );

      const pricing = await getBusAdminPrice({
        supplierFareTotal,
        supplierTaxTotal: 0,
        passengerCount: selectedSeats.length,
        seatCount: selectedSeats.length,
        busId: bus.id,
        travelDate: date?.iso || bus.departureDateTime?.slice(0, 10),
      });

      if (!cancelled) {
        setAdminPreviewTotal(Number(pricing.payableTotal));
      }
    } catch (error) {
      console.error("[BUS ADMIN PRICE PREVIEW]", error);
      if (!cancelled) {
        setAdminPreviewTotal(null);
      }
    } finally {
      if (!cancelled) {
        setAdminPricingLoading(false);
      }
    }
  }

  updateAdminPreview();

  return () => {
    cancelled = true;
  };
}, [selectedSeats, bus.id, date?.iso, bus.departureDateTime]);

  const toggleSeat = (seat) => {
    setSelectedSeats((curr) =>
      curr.some((s) => s.code === seat.code)
        ? curr.filter((s) => s.code !== seat.code)
        : [...curr, seat]
    );
  };

  useEffect(() => {
    if (!searchTrackId) {
      setLayoutError("Missing search session. Please search again.");
      setLayoutLoading(false);
      return;
    }

    const doj = bus.departureDateTime
      ? bus.departureDateTime.slice(0, 10)
      : date?.iso || (() => {
        const n = new Date();
        return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
      })();

    fetchSeatLayout({ tripId: bus.id, searchTrackId, doj })
      .then((data) => {
        const seatMap = data.seatMap || data.seats || [];

        const adaptSeat = (s) => {
  const isSleeper =
    Number(s.height) > 1 ||
    Number(s.width) > 1;

  return {
    code: s.name,
    seatId: s.name,

    price:
      Number(s.fare?.totalFare) ||
      Number(s.fare?.baseFare) ||
      0,

    ladies:
      s.ladiesSeat === "true" ||
      s.ladiesSeat === true,

    booked:
      s.available === "false" ||
      s.available === false,

    deck: Number(s.z) === 0 ? "lower" : "upper",

    height: Number(s.height || 1),
    width: Number(s.width || 1),

    isSleeper,

    seatType: isSleeper
      ? "Sleeper"
      : "Seater",
  };
};

        const lower = seatMap.filter(s => Number(s.z) === 0).map(adaptSeat);
        const upper = seatMap.filter(s => Number(s.z) === 1).map(adaptSeat);

        setSeatLayout({ lower, upper });

        const bps = data.boardingTimes || [];
        const dps = data.droppingTimes || [];
        setBoardingPoints(bps);
        setDroppingPoints(dps);
        if (bps.length) setBoarding(bps[0]);
        if (dps.length) setDropping(dps[0]);
      })
      .catch((err) => {
        console.error("Seat layout error:", err);
        setLayoutError("Could not load seat layout. Please try again.");
      })
      .finally(() => setLayoutLoading(false));
  }, [bus.id, searchTrackId]);

  const handleContinue = async () => {
    if (!selectedSeats.length) return;
    if (!boarding || !dropping) {
      alert("Please select a boarding and dropping point.");
      return;
    }
    try {
      const bpId = boarding?.bpId || boarding?.id || boarding?.boardingPointId;
      const dpId = dropping?.bpId || dropping?.id || dropping?.droppingPointId;

      const repriceResult = await repriceBus({
  searchTrackId,
  id: bus.id,
  bpId,
  dpId,
  selectedSeats: selectedSeats.map((s) => s.code),
});

const supplierConfirmedTotal = Number(
  repriceResult?.fareDetails?.totalFare ?? 0
);

if (!Number.isFinite(supplierConfirmedTotal) || supplierConfirmedTotal <= 0) {
  throw new Error("Supplier returned an invalid confirmed fare.");
}

const travelDate =
  date?.iso || bus.departureDateTime?.slice(0, 10);

const quoteResponse = await fetch(
  `${BUS_PRICING_API_BASE}/bus-bookings/quote`,
  {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      supplierFareTotal: supplierConfirmedTotal,
      supplierTaxTotal: 0,
      baseFare: repriceResult?.fareDetails?.baseFare,
      gst: repriceResult?.fareDetails?.taxes,
      discount: repriceResult?.fareDetails?.discount,
      seats: selectedSeats.map((seat) => seat.code),
      seatCount: selectedSeats.length,
      passengerCount: selectedSeats.length,
      travelDate,
      busId: String(bus.id ?? ""),
      searchKey: String(searchTrackId ?? ""),
    }),
  }
);

const quote = await quoteResponse.json();

if (
  !quoteResponse.ok ||
  !quote.quoteId ||
  !Number.isFinite(Number(quote.payableTotal))
) {
  throw new Error(
    quote.error || "Could not create the authoritative bus price quote."
  );
}

const adminPricing = quote;

setRepriceTotal(supplierConfirmedTotal);
setAdminPreviewTotal(Number(adminPricing.payableTotal));

const repriceData = {
  ...repriceResult,

  // Keep supplier values separate from the admin-adjusted payable total.
  totalFare: supplierConfirmedTotal,
  supplierFareTotal: supplierConfirmedTotal,
  baseFare: repriceResult?.fareDetails?.baseFare,
  gst: repriceResult?.fareDetails?.taxes,

  adminPricing,
  payableTotal: Number(adminPricing.payableTotal),
};

      const booking = createBusBookingPayload({
        bus, from, to, date, selectedSeats,
        boarding, dropping,
        repriceData,
        searchTrackId,
      });

      const fullBooking = {
  ...booking,
  searchTrackId,
  busId: bus.id,

  // Required by backend to persist the server-calculated price.
  quoteId: quote.quoteId,
  payableTotal: Number(quote.payableTotal),
  adminPayableTotal: Number(quote.payableTotal),
  adminPricing: quote,
  supplierFareTotal: supplierConfirmedTotal,
  boardingPointId: String(bpId ?? ""),
  droppingPointId: String(dpId ?? ""),

  // Keep the plain-string route values from createBusBookingPayload.
};

      saveBusBooking(fullBooking);
      navigate("/busbooking", { state: { booking: fullBooking } });
    } catch (err) {
      console.error("Reprice error:", err);
      alert("Could not confirm fare. Please try again.");
    }
  };

  const isLastStep = activeStep === "seats";
  const continueDisabled = isLastStep && (!selectedSeats.length || !boarding || !dropping);

  return (
    <div className="seat-modal-backdrop" role="dialog" aria-modal="true">
      <div className="seat-modal">
        <button type="button" className="seat-modal-close" onClick={onClose}>×</button>

        <div className="seat-modal-route">
          <strong>{from.split(",")[0]} <ArrowRight size={15} className="route-arrow" /> {to.split(",")[0]}</strong>
          <div className="seat-modal-meta">
            <span>{date.label}, {date.day} 2026</span>
            <span className="meta-dot" />
            <span>{bus.operator}</span>
            <span className="meta-dot" />
            <span>{bus.busType}</span>
          </div>
        </div>

        <div className="wizard-stepper">
          {steps.map((s, i) => (
            <button
              key={s.key}
              type="button"
              disabled={i > maxStepReached}
              className={[
                "wizard-step",
                activeStep === s.key ? "active" : "",
                i < stepIndex ? "done" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => goToStep(s.key)}
            >
              <span className="wizard-step-num">{i < stepIndex ? "✓" : i + 1}</span>
              {s.label}
            </button>
          ))}
        </div>

        {layoutLoading ? (
          <div style={{ padding: "2rem", textAlign: "center" }}><Bus size={22} />
            <span>Loading your trip...</span></div>
        ) : layoutError ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "red" }}>{layoutError}</div>
        ) : (
          <>
            <div className="seat-modal-body-scroll">
              {activeStep === "busInfo" && <BusInfoPanel bus={bus} />}
              {activeStep === "seatInfo" && <SeatLegendPanel />}

              {activeStep === "seats" && (
                <div className="wizard-panel">
                  <div className="seat-price-row">
                    <strong>Filter Price</strong>
                    {priceOptions.map((price) => (
                      <button key={price} type="button"
                        className={priceFilter === price ? "active" : ""}
                        onClick={() => setPriceFilter(price)}>
                        {price === "All" ? "All" : `₹${price}`}
                      </button>
                    ))}
                  </div>

                  <div className="seat-legend">
  <span><i className="ladies" /> Ladies</span>
  <span><i className="selected" /> Selected</span>
  <span><i /> Available</span>
  <span><i className="booked" /> Booked</span>

  <span>
    <Armchair size={13} /> Seater
  </span>

  <span>
    <BedDouble size={13} /> Sleeper
  </span>
</div>

                  <div className="deck-wrap">
                    {visibleLower.length > 0 && (
                      <SeatDeck title="LOWER DECK" priceFrom={lowerMinPrice} seats={visibleLower} selectedSeats={selectedSeats} onToggle={toggleSeat} />
                    )}
                    {visibleUpper.length > 0 && (
                      <SeatDeck title="UPPER DECK" priceFrom={upperMinPrice} seats={visibleUpper} selectedSeats={selectedSeats} onToggle={toggleSeat} />
                    )}
                  </div>

                  <div className="point-section">
                    <h4 className="wizard-subhead">Boarding &amp; Drop Point</h4>
                    <div className="point-tabs">
                      <div role="button" tabIndex={0}
                        className={pointTab === "boarding" ? "active" : ""}
                        onClick={() => setPointTab("boarding")}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setPointTab("boarding"); }}>
                        BOARDING POINT
                      </div>
                      <div role="button" tabIndex={0}
                        className={pointTab === "dropping" ? "active" : ""}
                        onClick={() => setPointTab("dropping")}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setPointTab("dropping"); }}>
                        DROPPING POINT
                      </div>
                    </div>
                    <div className="point-options">
                      {(pointTab === "boarding" ? boardingPoints : droppingPoints).map((point, index) => {
                        const isSelected = pointTab === "boarding"
                          ? boarding?.bpId === point.bpId || boarding?.id === point.id
                          : dropping?.bpId === point.bpId || dropping?.id === point.id;
                        return (
                          <label key={point.id || index} className={`point-option ${isSelected ? "selected" : ""}`}>
                            <input
                              type="radio"
                              className="point-radio"
                              checked={isSelected}
                              onChange={() => pointTab === "boarding" ? setBoarding(point) : setDropping(point)}
                            />
                            <span className="point-radio-dot" />
                            <span className="point-body">
                              <strong>{point.time ? new Date(point.time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }) : "--"}</strong>
                              <em>{point.name}</em>
                              <small>{point.landmark || point.address || ""}</small>
                              {point.prime === "true" && <b className="prime-tag">★ Main</b>}
                            </span>
                          </label>
                        );
                      })}
                      {(pointTab === "boarding" ? boardingPoints : droppingPoints).length === 0 && (
                        <p style={{ padding: "0.5rem", color: "var(--slate-400)" }}>No points available.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="wizard-footer">
              {isLastStep && (
                <div className="wizard-footer-summary">
                  <span>
  Seats:{" "}
  <strong>
    {selectedSeats.length
      ? selectedSeats
          .map((s) => `${s.code} (${s.seatType})`)
          .join(", ")
      : "—"}
  </strong>
</span>
                 <span>
  Total:{" "}
  <strong>
    {selectedSeats.length
      ? adminPricingLoading
        ? "Calculating..."
        : adminPreviewTotal != null
          ? `₹${adminPreviewTotal.toLocaleString("en-IN")}`
          : `₹${total.toLocaleString("en-IN")}`
      : "—"}
  </strong>
</span>
                </div>
              )}
              <div className="wizard-footer-actions">
                {stepIndex > 0 && (
                  <button type="button" className="wizard-back-btn" onClick={goBack}>← Back</button>
                )}
                <button
                  type="button"
                  className="seat-continue"
                  disabled={continueDisabled}
                  onClick={isLastStep ? handleContinue : goNext}
                >
                  {isLastStep ? "Continue to Booking" : "Continue"}
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Compact Bus Card ───────────────────────────────────────────────────────
function CompactBusCard({ bus, onSelect }) {
  const displayedPrice = Number(bus.displayPrice ?? bus.price ?? 0);

const discountPct = bus.originalPrice > displayedPrice
  ? Math.round(
      ((bus.originalPrice - displayedPrice) / bus.originalPrice) * 100
    )
  : 0;

  return (
    <div className="bus-compact-card" style={{ display: 'grid' }}>
      <div className="bcc-operator">
        <div className="bcc-op-icon">{bus.operator.charAt(0)}</div>
        <div className="bcc-op-info">
          <div className="bcc-op-name">{bus.operator}</div>
          <div className="bcc-op-type">{bus.busType}</div>
        </div>
      </div>

      <div className="bcc-journey">
        <div className="bcc-time-block">
          <div className="bcc-time">{bus.departure}</div>
          <div className="bcc-city">{bus.departureCity?.split(",")[0]}</div>
        </div>
        <div className="bcc-route-line">
          <div className="bcc-duration-label">{bus.duration}</div>
          <div className="bcc-line-track">
            <div className="bcc-dot start" />
            <div className="bcc-line" />
            <div className="bcc-dot end" />
          </div>
          <div className="bcc-type-label">
  {bus.isAC ? "AC" : "Non-AC"}
  {" · "}
  {bus.seater && bus.sleeper
    ? "Seater · Sleeper"
    : bus.sleeper
      ? "Sleeper"
      : "Seater"}
</div>
        </div>
        <div className="bcc-time-block" style={{ textAlign: 'right' }}>
          <div className="bcc-time">{bus.arrival}</div>
          <div className="bcc-city">{bus.arrivalCity?.split(",")[0]}</div>
        </div>
      </div>

      <div className="bcc-divider" />

      <div className="bcc-meta">
        <div className="bcc-badges">
          <span className={`bcc-badge ${bus.isAC ? 'ac' : ''}`}>
            {bus.isAC ? <Snowflake size={10} /> : <Wind size={10} />}
            {bus.isAC ? ' AC' : ' Non-AC'}
          </span>

          {bus.seater && (
  <span className="bcc-badge seater">
    <Armchair size={10} />
    {" "}Seater
  </span>
)}

{bus.sleeper && (
  <span className="bcc-badge sleeper">
    <BedDouble size={10} />
    {" "}Sleeper
  </span>
)}
          {bus.hasGPS && (
            <span className="bcc-badge gps">
              <Satellite size={10} />
              GPS
            </span>
          )}

        </div>
        <div className="bcc-seats">
          <span className={bus.seatsLeft <= 5 ? 'bcc-seats-urgent' : ''}>
            <>
              <AlertTriangle size={12} />
              {bus.seatsLeft <= 5
                ? `Only ${bus.seatsLeft} left`
                : `${bus.seatsLeft} seats`}
            </>
          </span>
          {bus.singleSeats > 0 && <span style={{ color: 'var(--slate-400)', marginLeft: 4 }}>· {bus.singleSeats} single</span>}
        </div>
        <RatingBadge rating={bus.rating} />
      </div>

      <div className="bcc-price-action">
        <div className="bcc-price">
          {bus.originalPrice > displayedPrice && (
  <span className="bcc-price-orig">
    ₹{bus.originalPrice.toLocaleString("en-IN")}
  </span>
)}
          <span className="bcc-price-now">
  ₹{Number(bus.displayPrice ?? bus.price).toLocaleString("en-IN")}
</span>
          {discountPct > 0 && <span className="bcc-discount">{discountPct}% OFF</span>}
        </div>
        <button type="button" className="bcc-select-btn" onClick={() => onSelect(bus)}>
          Book Now →
        </button>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function BusesPage() {
  usePageMeta(
  "Book Bus Tickets Online | AC, Sleeper & Volvo Buses – MakeBeeTrip",
  "Book AC, sleeper and Volvo bus tickets online with MakeBeeTrip. Compare operators, choose your seats, view amenities and travel across India with ease.",
  "bus booking, bus tickets online, sleeper bus booking, volvo bus tickets, ac bus booking, bus seat selection, bus operators, intercity bus"
);
  const dateStrip = useMemo(() => getDateStrip(), []);
  const { cities, getCityId, getAllCities, loading: citiesLoading } = useCities();

  // ── All city names for autocomplete ──────────────────────────────────────
  const allCityNames = useMemo(() => {
    if (typeof getAllCities === "function") return getAllCities();
    return [];
  }, [getAllCities, cities]);

  const [apiBuses, setApiBuses] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchTrackId, setSearchTrackId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [defaultBuses, setDefaultBuses] = useState([]);
  const [defaultLoading, setDefaultLoading] = useState(true);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [dropPoints, setDropPoints] = useState([]);
  const [operators, setOperators] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  // ── Search state ──────────────────────────────────────────────────────────
  const [from, setFrom] = useState("Mumbai");
  const [to, setTo] = useState("Pune");
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const calendarRef = useRef(null);

  // Date logic — untouched. selectedDate remains an ISO string "YYYY-MM-DD".
  const [selectedDate, setSelectedDate] = useState(getLocalToday());
  const [compareList, setCompareList] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const toggleCompare = (bus) => {
    setCompareList((prev) => {
      const exists = prev.some((b) => b.id === bus.id);
      if (exists) return prev.filter((b) => b.id !== bus.id);
      if (prev.length >= 3) return prev; // cap at 3
      return [...prev, bus];
    });
  };

  const removeCompare = (id) => {
    if (id === "all") { setCompareList([]); setCompareOpen(false); return; }
    setCompareList((prev) => prev.filter((b) => b.id !== id));
  };
  useEffect(() => {
    if (!calendarOpen) return;

    const handleOutsideClick = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [calendarOpen]);

  // ── Load default buses on mount ───────────────────────────────────────────
  useEffect(() => {
    if (citiesLoading) return;

    const loadDefault = async () => {
      try {
        setDefaultLoading(true);
        const sourceId = getCityId(from);
        const destinationId = getCityId(to);
        if (!sourceId || !destinationId) { setDefaultLoading(false); return; }

        const doj = selectedDate || getLocalToday();
        console.log("[SEARCH DEBUG] from:", from, "→ sourceId:", sourceId);
        console.log("[SEARCH DEBUG] to:", to, "→ destinationId:", destinationId);
        const result = await searchBuses({ sourceId, destinationId, doj });
        const busArray = result.availableTrips || result.buses || result.data || [];
        const adapted = busArray.map((raw) =>
  normaliseBus({
    ...adaptApiBus(raw),
    seater: raw.seater,
    sleeper: raw.sleeper,
  })
);

const pricedBuses = await applyBusPricingPreview(adapted, doj);

pricedBuses.sort((a, b) => b.rating - a.rating);
setDefaultBuses(pricedBuses);
        setSearchTrackId(result.searchTrackId || null);

        // ── Extract pickup/drop points from API results ──
        const allBoarding = adapted.flatMap(b => b.boardingPoints ?? []);
        const allDropping = adapted.flatMap(b => b.droppingPoints ?? []);
        setPickupPoints([...new Set(allBoarding.map(p => p.name).filter(Boolean))]);
        setDropPoints([...new Set(allDropping.map(p => p.name).filter(Boolean))]);
        setOperators([...new Set(adapted.map(b => b.operator).filter(Boolean))]);

      } catch (err) {
        console.error("Default buses load failed:", err);
      } finally {
        setDefaultLoading(false);
      }
    };

    loadDefault();
  }, [citiesLoading]);

  async function applyBusPricingPreview(buses, travelDate) {
  return Promise.all(
    buses.map(async (bus) => {
      try {
        const response = await fetch(
          `${BUS_PRICING_API_BASE}/bus-bookings/pricing-preview`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              supplierFareTotal: Number(bus.supplierPrice ?? bus.price ?? 0),
              supplierTaxTotal: 0,
              passengerCount: 1,
              seatCount: 1,
              busId: String(bus.id ?? ""),
              travelDate,
            }),
          }
        );

        const data = await response.json();

        if (!response.ok || !Number.isFinite(Number(data.payableTotal))) {
          console.error(
            "[BUS PRICING PREVIEW] Failed for bus:",
            bus.id,
            data.error || response.status
          );

          return {
            ...bus,
            displayPrice: Number(bus.supplierPrice ?? bus.price ?? 0),
            pricingPreviewFailed: true,
          };
        }

        return {
          ...bus,
          displayPrice: Number(data.payableTotal),
          pricingPreviewFailed: false,
        };
      } catch (error) {
        console.error("[BUS PRICING PREVIEW] Request failed:", error);

        return {
          ...bus,
          displayPrice: Number(bus.supplierPrice ?? bus.price ?? 0),
          pricingPreviewFailed: true,
        };
      }
    })
  );
}

  // ── Normalise adapted bus to guarantee all filter fields exist ────────────
  function normaliseBus(bus) {
    const busTypeText = String(bus.busType || "").toLowerCase();

const supplierSeater =
  bus.seater === true || bus.seater === "true"
    ? true
    : bus.seater === false || bus.seater === "false"
      ? false
      : null;

const supplierSleeper =
  bus.sleeper === true || bus.sleeper === "true"
    ? true
    : bus.sleeper === false || bus.sleeper === "false"
      ? false
      : null;

const seater =
  supplierSeater !== null
    ? supplierSeater
    : busTypeText.includes("seater");

const sleeper =
  supplierSleeper !== null
    ? supplierSleeper
    : busTypeText.includes("sleeper");

    return {
      amenities: bus.amenities ?? [],
      tags: bus.tags ?? [],
      category: bus.category ?? "",
      seater,
    sleeper,
      seatType: bus.seatType ?? (bus.busType?.toLowerCase().includes("sleeper") ? "sleeper" : "seater"),
      isAC: bus.isAC ?? bus.busType?.toLowerCase().includes("ac") ?? false,
      hasGPS: bus.hasGPS ?? false,
      rating: bus.rating ?? 3.5,
      totalRatings: bus.totalRatings ?? 0,
      seatsLeft: bus.seatsLeft ?? bus.availableSeats ?? 0,
      singleSeats: bus.singleSeats ?? 0,
      // ✅ Single price declaration — no duplicates
      supplierPrice: Number(
  bus.supplierPrice ?? bus.price ?? bus.fare ?? bus.baseFare ?? 0
),
price: Number(
  bus.supplierPrice ?? bus.price ?? bus.fare ?? bus.baseFare ?? 0
),
displayPrice: Number(
  bus.supplierPrice ?? bus.price ?? bus.fare ?? bus.baseFare ?? 0
),
originalPrice: Number(
  bus.originalPrice ?? bus.mrp ?? bus.price ?? bus.fare ?? bus.baseFare ?? 0
),
      durationMins: bus.durationMins ?? 0,
      departure: bus.departure ?? "",
      arrival: bus.arrival ?? "",
      departureCity: bus.departureCity ?? "",
      arrivalCity: bus.arrivalCity ?? "",
      busType: bus.busType ?? "",
      operator: bus.operator ?? "Unknown",
      duration: bus.duration ?? "",
      id: bus.id,
      departureDateTime: bus.departureDateTime ?? "",
      boardingPoints: bus.boardingPoints ?? [],
      droppingPoints: bus.droppingPoints ?? [],
      photos: bus.photos ?? [],
      policies: bus.policies ?? [],
      reviewsList: bus.reviewsList ?? [],
      departureSlot: getDepartureSlot(bus.departureDateTime || bus.departure),
    };
  }

  // ── Handle search ─────────────────────────────────────────────────────────
  const handleSearch = useCallback(async (overrideDate) => {
    const sourceId = getCityId(from);
    const destinationId = getCityId(to);
    const doj = overrideDate || selectedDate;

    if (!sourceId || !destinationId) {
      setSearchError(`City not found: "${!sourceId ? from : to}". Please pick from the dropdown.`);
      return;
    }

    setSearchLoading(true);
    setSearchError(null);

    try {
      console.log("[SEARCH DEBUG] from:", from, "→ sourceId:", sourceId);
      console.log("[SEARCH DEBUG] to:", to, "→ destinationId:", destinationId);
      const result = await searchBuses({ sourceId, destinationId, doj });
      const busArray = result.availableTrips || result.buses || result.data || [];
      const buses = busArray.map((raw) =>
  normaliseBus({
    ...adaptApiBus(raw),
    seater: raw.seater,
    sleeper: raw.sleeper,
  })
);

const pricedBuses = await applyBusPricingPreview(buses, doj);

setApiBuses(pricedBuses);
      setHasSearched(true);
      setSearchTrackId(result.searchTrackId || null);

      setPickupPoints([...new Set(buses.flatMap(b => b.boardingPoints ?? []).map(p => p.name).filter(Boolean))]);
      setDropPoints([...new Set(buses.flatMap(b => b.droppingPoints ?? []).map(p => p.name).filter(Boolean))]);
      setOperators([...new Set(buses.map(b => b.operator).filter(Boolean))]);
      navigate(`/buses?${new URLSearchParams({ from, to, date: doj }).toString()}`);
    } catch (err) {
      console.error("Search error:", err);
      setSearchError("Search failed. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  }, [from, to, selectedDate, getCityId, navigate]);

  // ── Re-search when date is picked (if already searched) ──
  const handleDateSelect = (iso) => {
    setSelectedDate(iso);
    if (hasSearched) handleSearch(iso);
  };

  // ── Sync URL params on mount ──────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromParam = params.get("from");
    const toParam = params.get("to");
    const dateParam = params.get("date");
    if (fromParam) setFrom(fromParam);
    if (toParam) setTo(toParam);
    if (dateParam) {
      const inStrip = dateStrip.find((d) => d.iso === dateParam);
      setSelectedDate(inStrip ? inStrip.iso : dateParam);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filterAC, setFilterAC] = useState([]);
  const [filterSeatType, setFilterSeatType] = useState([]);
  const [filterDeparture, setFilterDeparture] = useState([]);
  const [filterRating, setFilterRating] = useState(0);
  const [filterGPS, setFilterGPS] = useState(false);
  // FIX: raised default max price to 20000 so no bus is silently excluded
  const [filterPriceMax, setFilterPriceMax] = useState(20000);
  const [filterPickup, setFilterPickup] = useState([]);
  const [filterDrop, setFilterDrop] = useState([]);
  const [filterAmenities, setFilterAmenities] = useState([]);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterOperators, setFilterOperators] = useState([]);
  const [filterPickupTime, setFilterPickupTime] = useState([]);
  const [filterDropTime, setFilterDropTime] = useState([]);
  const [filterSingleSeat, setFilterSingleSeat] = useState(false);
  const [sortBy, setSortBy] = useState("relevance");
  const BUSES_PER_PAGE = 15;
const [currentPage, setCurrentPage] = useState(1);

  // "Show all" expand toggles for long point/operator lists
  const [showAllPickup, setShowAllPickup] = useState(false);
  const [showAllDrop, setShowAllDrop] = useState(false);
  const [showAllOperators, setShowAllOperators] = useState(false);

  // UI state
  const [expandedId, setExpandedId] = useState(null);
  const [expandedTab, setExpandedTab] = useState("boarding");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [pickupSearch, setPickupSearch] = useState("");
  const [dropSearch, setDropSearch] = useState("");
  const [bookingBus, setBookingBus] = useState(null);

  const swapCities = () => { setFrom(to); setTo(from); };

  // ── Compute dynamic max price for slider ──────────────────────────────────
  const allBuses = hasSearched ? apiBuses : defaultBuses;
  const maxPrice = useMemo(() => {
    const prices = allBuses
  .map((b) => Number(b.displayPrice ?? b.price))
  .filter((price) => Number.isFinite(price) && price > 0);
    return prices.length ? Math.ceil(Math.max(...prices) / 500) * 500 : 20000;
  }, [allBuses]);
  // Which optional filter groups actually have data — used to hide empty sections
  const hasAnyGPS = useMemo(() => allBuses.some(b => b.hasGPS), [allBuses]);
  const hasAnyAC = useMemo(() => allBuses.some(b => b.isAC), [allBuses]);
  const hasAnyNonAC = useMemo(() => allBuses.some(b => !b.isAC), [allBuses]);
  const hasAnySleeper = useMemo(
  () => allBuses.some((b) => b.sleeper),
  [allBuses]
);

const hasAnySeater = useMemo(
  () => allBuses.some((b) => b.seater),
  [allBuses]
);
  const hasAnyRating = useMemo(() => allBuses.some(b => b.rating > 0), [allBuses]);
  const hasAnyAmenities = useMemo(
    () => allBuses.some(b => (b.amenities || []).length > 0),
    [allBuses]
  );
  const hasAnySingleSeats = useMemo(
    () => allBuses.some(b => (b.singleSeats || 0) > 0),
    [allBuses]
  );
  const pickupTimeBuckets = useMemo(() => {
    const buckets = new Set();
    allBuses.forEach(b => buckets.add(getDepartureSlot(b.departureDateTime || b.departure)));
    return buckets;
  }, [allBuses]);
  const dropTimeBuckets = useMemo(() => {
    const buckets = new Set();
    allBuses.forEach(b => buckets.add(getDepartureSlot(b.arrival)));
    return buckets;
  }, [allBuses]);
  // ── Filtered + sorted list ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let list = [...allBuses];

    // AC filter
    if (filterAC.length)
      list = list.filter((b) =>
        (filterAC.includes("ac") && b.isAC) ||
        (filterAC.includes("nonac") && !b.isAC)
      );

    // Seat type filter
    if (filterSeatType.length) {
  list = list.filter((b) =>
    (filterSeatType.includes("seater") && b.seater) ||
    (filterSeatType.includes("sleeper") && b.sleeper)
  );
}

    // FIX: departureSlot is now set on each bus object
    if (filterDeparture.length)
      list = list.filter((b) => filterDeparture.includes(b.departureSlot));

    // Rating filter
    if (filterRating > 0)
      list = list.filter((b) => b.rating >= filterRating);

    // GPS filter
    if (filterGPS)
      list = list.filter((b) => b.hasGPS);

    // Price filter — only apply when user has moved the slider
    if (filterPriceMax < maxPrice) {
  list = list.filter(
    (b) => Number(b.displayPrice ?? b.price) <= filterPriceMax
  );
}

    // Category / tags filter
    if (filterCategory.length)
      list = list.filter((b) =>
        filterCategory.some((c) => (b.tags || []).includes(c) || b.category === c)
      );

    // Amenities — every selected amenity must be present
    if (filterAmenities.length)
      list = list.filter((b) =>
        filterAmenities.every((a) => (b.amenities || []).includes(a))
      );
    // Single seat availability
    if (filterSingleSeat)
      list = list.filter((b) => (b.singleSeats || 0) > 0);

    // Operators
    if (filterOperators.length)
      list = list.filter((b) => filterOperators.includes(b.operator));

    // Pickup time bucket (uses same slot logic as departure)
    if (filterPickupTime.length)
      list = list.filter((b) =>
        filterPickupTime.includes(getDepartureSlot(b.departureDateTime || b.departure))
      );

    // Drop time bucket
    if (filterDropTime.length)
      list = list.filter((b) =>
        filterDropTime.includes(getDepartureSlot(b.arrival))
      );

    // Pickup point
    if (filterPickup.length)
      list = list.filter((b) =>
        (b.boardingPoints || []).some((p) => filterPickup.includes(p.name))
      );

    // Drop point
    if (filterDrop.length)
      list = list.filter((b) =>
        (b.droppingPoints || []).some((p) => filterDrop.includes(p.name))
      );
    // Sort
    switch (sortBy) {
      case "price-asc":
  list.sort(
    (a, b) =>
      Number(a.displayPrice ?? a.price) -
      Number(b.displayPrice ?? b.price)
  );
  break;

case "price-desc":
  list.sort(
    (a, b) =>
      Number(b.displayPrice ?? b.price) -
      Number(a.displayPrice ?? a.price)
  );
  break;
      case "rating": list.sort((a, b) => b.rating - a.rating); break;
      case "fastest": list.sort((a, b) => a.durationMins - b.durationMins); break;
      case "departure": list.sort((a, b) => a.departure.localeCompare(b.departure)); break;
      case "arrival": list.sort((a, b) => a.arrival.localeCompare(b.arrival)); break;
      default: break;
    }
    return list;
  }, [allBuses, filterAC, filterSeatType, filterDeparture,
    filterRating, filterGPS, filterPriceMax, maxPrice,
    filterCategory, filterAmenities, sortBy,
    filterSingleSeat, filterOperators, filterPickupTime, filterDropTime,
    filterPickup, filterDrop]);

    const totalPages = Math.max(
  1,
  Math.ceil(filtered.length / BUSES_PER_PAGE)
);

const safeCurrentPage = Math.min(currentPage, totalPages);

const paginatedBuses = useMemo(() => {
  const startIndex = (safeCurrentPage - 1) * BUSES_PER_PAGE;

  return filtered.slice(
    startIndex,
    startIndex + BUSES_PER_PAGE
  );
}, [filtered, safeCurrentPage]);

const visibleBusStart =
  filtered.length === 0
    ? 0
    : (safeCurrentPage - 1) * BUSES_PER_PAGE + 1;

const visibleBusEnd = Math.min(
  safeCurrentPage * BUSES_PER_PAGE,
  filtered.length
);

useEffect(() => {
  setCurrentPage(1);
}, [
  sortBy,
  filterAC,
  filterSeatType,
  filterDeparture,
  filterRating,
  filterGPS,
  filterPriceMax,
  filterPickup,
  filterDrop,
  filterAmenities,
  filterCategory,
  filterOperators,
  filterPickupTime,
  filterDropTime,
  filterSingleSeat,
  selectedDate,
]);

const goToBusPage = (page) => {
  const nextPage = Math.min(
    Math.max(page, 1),
    totalPages
  );

  setCurrentPage(nextPage);

  requestAnimationFrame(() => {
    document.querySelector(".results-toolbar")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  });
};

  const clearAll = () => {
    setFilterAC([]); setFilterSeatType([]); setFilterDeparture([]);
    setFilterRating(0); setFilterGPS(false); setFilterPriceMax(maxPrice);
    setFilterPickup([]); setFilterDrop([]); setFilterAmenities([]);
    setFilterCategory([]); setSortBy("relevance");
    setFilterOperators([]); setFilterPickupTime([]); setFilterDropTime([]);
    setFilterSingleSeat(false);
  };

  const activeFilterCount = [
    filterAC.length, filterSeatType.length, filterDeparture.length,
    filterRating > 0 ? 1 : 0, filterGPS ? 1 : 0,
    filterPriceMax < maxPrice ? 1 : 0, filterCategory.length, filterAmenities.length,
    filterOperators.length, filterPickupTime.length, filterDropTime.length,
    filterSingleSeat ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setExpandedTab("boarding");
  };

  // ── Derive the active date label for the searchbar date field ─────────────
  const activeDateObj = dateStrip.find((d) => d.iso === selectedDate) || dateStrip[0];

  return (
    <div className="bp-root">
      {bookingBus && (
        <BusSeatModal
          bus={bookingBus}
          from={from}
          to={to}
          date={activeDateObj}
          searchTrackId={searchTrackId}
          onClose={() => setBookingBus(null)}
        />
      )}

      {/* ── Header with CSS bus scene ── */}
      <header className="bp-header">
        <div className="bp-header-stars" aria-hidden="true">
          {[...Array(12)].map((_, i) => (
            <span key={i} style={{
              left: `${8 + i * 8}%`,
              top: `${10 + (i % 3) * 25}%`,
              animationDelay: `${i * 0.4}s`,
            }} />
          ))}
        </div>

        <div className="bp-bus-scene" aria-hidden="true">
          <div className="bus-road" />
          <div className="bus-body" />
          <div className="bus-wheel front" />
          <div className="bus-wheel rear" />
          <div className="bus-exhaust">
            <span /><span /><span />
          </div>
        </div>

        {/* ── Card-based search bar: FROM · connector · TO · DATE · SEARCH ── */}
        <div className="bp-searchbar">
          <CityAutocomplete
            label="FROM"
            value={from}
            onChange={setFrom}
            placeholder="Origin city"
            allCities={allCityNames}
            icon={<MapPinned size={16} />}
          />

          <div className="bp-connector">
            <span className="bp-connector-line" />
            <button className="swap-btn" onClick={swapCities} title="Swap cities">
              <ChevronsRight size={16} />
            </button>
            <span className="bp-connector-line" />
          </div>

          <CityAutocomplete
            label="TO"
            value={to}
            onChange={setTo}
            placeholder="Destination city"
            allCities={allCityNames}
            icon={<MapPin size={16} />}
          />

          <div className="bp-search-field bp-date-card-wrap">
            <div className="bus-date-card" onClick={() => setCalendarOpen((prev) => !prev)}>
              <span className="bus-date-label">DATE</span>
              <div className="bus-date-card-icon"><Calendar size={16} /></div>
              <div className="bus-date-card-text">
                <span className="bus-date-card-day">
                  {formatBusDate(selectedDate).day}
                  <small className="bus-date-card-month-inline">{formatBusDate(selectedDate).month}</small>
                </span>
                <span className="bus-date-card-weekday">{formatBusDate(selectedDate).weekday}</span>
              </div>
            </div>

            {calendarOpen && (
              <div className="bus-calendar-popover" ref={calendarRef}>
                <div className="bus-calendar-header-row">
                  <button
                    type="button"
                    className="bus-calendar-nav"
                    onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                  >
                    ‹
                  </button>
                  <div className="bus-calendar-title">{getMonthLabel(calendarMonth)}</div>
                  <button
                    type="button"
                    className="bus-calendar-nav"
                    onClick={() => setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                  >
                    ›
                  </button>
                </div>
                <div className="bus-calendar-weekdays">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <div key={d} className="bus-calendar-weekday">{d}</div>
                  ))}
                </div>
                <div className="bus-calendar-grid">
                  {getMonthGrid(calendarMonth).map((day) => {
                    const selected = day.iso === selectedDate;
                    const disabled = !day.inMonth;
                    return (
                      <button
                        key={day.iso}
                        type="button"
                        className={`bus-calendar-day${selected ? " selected" : ""}${disabled ? " inactive" : ""}`}
                        onClick={() => {
                          if (disabled) return;
                          setSelectedDate(day.iso);
                          setCalendarOpen(false);
                        }}
                        disabled={disabled}
                      >
                        <span className="bus-calendar-day-number">{day.day}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <input
              id="bus-date-input"
              type="date"
              value={selectedDate}
              min={dateStrip[0].iso}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bus-date-input"
            />
          </div>

          <button className="search-btn" onClick={() => handleSearch()} disabled={searchLoading}>
            <Search size={16} style={{ marginRight: 8 }} />
            {searchLoading ? "Searching..." : "Search Bus"}
          </button>
          {searchError && <p className="search-error">{searchError}</p>}
        </div>
      </header>


      {/* ── Body: Sidebar + Results ── */}
      <div className={`bp-body ${filtersOpen ? "filters-open" : "filters-closed"}`}>

        {/* ── Sticky Sidebar ── */}
        {filtersOpen && (
          <div className="bp-sidebar-overlay" onClick={() => setFiltersOpen(false)} />
        )}
        <aside className={`bp-sidebar ${filtersOpen ? "open" : "closed"}`}>
          <div className="sidebar-head">
            <h3>
              Filters
              {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
            </h3>
            <button className="clear-all-btn" onClick={clearAll}>Clear All</button>
          </div>
          <CheckboxGroup
            label="Bus Type"
            options={[
              { value: "ac", label: <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Snowflake size={12} /> AC</span> },
              { value: "nonac", label: <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Wind size={12} /> Non-AC</span> },
            ].filter(opt => (opt.value === "ac" ? hasAnyAC : hasAnyNonAC))}
            selected={filterAC}
            onChange={setFilterAC}
          />

          <CheckboxGroup
            label="Seat Type"
            options={[
              { value: "sleeper", label: <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><BedDouble size={12} /> Sleeper</span> },
              { value: "seater", label: <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Armchair size={12} /> Seater</span> },
            ]}
            selected={filterSeatType}
            onChange={setFilterSeatType}
          />

          {hasAnySingleSeats && (
            <div className="filter-group">
              <h4 className="filter-label">Single Seater/Sleeper</h4>
              <label className="toggle-row">
                <span>Single Seats</span>
                <input
                  type="checkbox"
                  className="toggle-input"
                  checked={filterSingleSeat}
                  onChange={(e) => setFilterSingleSeat(e.target.checked)}
                />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
              </label>
              <p style={{ fontSize: 11, color: "var(--slate-400)", margin: "4px 0 0" }}>
                Separate single window seats
              </p>
            </div>
          )}

          {pickupPoints.length > 0 && (
            <div className="filter-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="filter-label">Pick up point - {from.split(",")[0]}</h4>
                {filterPickup.length > 0 && (
                  <button className="clear-all-btn" onClick={() => setFilterPickup([])}>CLEAR</button>
                )}
              </div>
              <div className="point-search-wrap">
                <input
                  className="point-search"
                  placeholder="Search pickup..."
                  value={pickupSearch}
                  onChange={(e) => setPickupSearch(e.target.value)}
                />
              </div>
              <div className="point-list">
                {pickupPoints
                  .filter((p) => p.toLowerCase().includes(pickupSearch.toLowerCase()))
                  .slice(0, showAllPickup ? undefined : 4)
                  .map((p) => (
                    <label key={p} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filterPickup.includes(p)}
                        onChange={() =>
                          setFilterPickup((prev) =>
                            prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
                          )
                        }
                      />
                      <span className="custom-check" />
                      <MapPin size={11} style={{ flexShrink: 0, color: "var(--em)" }} />
                      {p}
                    </label>
                  ))}
              </div>
              {pickupPoints.length > 4 && (
                <button className="clear-all-btn" onClick={() => setShowAllPickup((s) => !s)}>
                  {showAllPickup ? "Show less" : `Show all (${pickupPoints.length})`}
                </button>
              )}
            </div>
          )}

          {pickupTimeBuckets.size > 0 && (
            <div className="filter-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="filter-label">Pick up time - {from.split(",")[0]}</h4>
                {filterPickupTime.length > 0 && (
                  <button className="clear-all-btn" onClick={() => setFilterPickupTime([])}>CLEAR</button>
                )}
              </div>
              <div className="checkbox-list">
                {[
                  { value: "before6", label: "12 AM - 6 AM" },
                  { value: "6to12", label: "6 AM - 12 PM" },
                  { value: "12to18", label: "12 PM - 6 PM" },
                  { value: "after18", label: "6 PM - 12 AM" },
                ].filter(({ value }) => pickupTimeBuckets.has(value))
                  .map(({ value, label }) => (
                    <label key={value} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filterPickupTime.includes(value)}
                        onChange={() =>
                          setFilterPickupTime((prev) =>
                            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
                          )
                        }
                      />
                      <span className="custom-check" />
                      {label}
                    </label>
                  ))}
              </div>
            </div>
          )}

          {operators.length > 0 && (
            <div className="filter-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="filter-label">Operators</h4>
                {filterOperators.length > 0 && (
                  <button className="clear-all-btn" onClick={() => setFilterOperators([])}>CLEAR</button>
                )}
              </div>
              <div className="point-list">
                {operators
                  .slice(0, showAllOperators ? undefined : 4)
                  .map((op) => (
                    <label key={op} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filterOperators.includes(op)}
                        onChange={() =>
                          setFilterOperators((prev) =>
                            prev.includes(op) ? prev.filter((x) => x !== op) : [...prev, op]
                          )
                        }
                      />
                      <span className="custom-check" />
                      {op}
                    </label>
                  ))}
              </div>
              {operators.length > 4 && (
                <button className="clear-all-btn" onClick={() => setShowAllOperators((s) => !s)}>
                  {showAllOperators ? "Show less" : `Show all (${operators.length})`}
                </button>
              )}
            </div>
          )}

          {dropPoints.length > 0 && (
            <div className="filter-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="filter-label">Drop point - {to.split(",")[0]}</h4>
                {filterDrop.length > 0 && (
                  <button className="clear-all-btn" onClick={() => setFilterDrop([])}>CLEAR</button>
                )}
              </div>
              <div className="point-search-wrap">
                <input
                  className="point-search"
                  placeholder="Search drop..."
                  value={dropSearch}
                  onChange={(e) => setDropSearch(e.target.value)}
                />
              </div>
              <div className="point-list">
                {dropPoints
                  .filter((p) => p.toLowerCase().includes(dropSearch.toLowerCase()))
                  .slice(0, showAllDrop ? undefined : 4)
                  .map((p) => (
                    <label key={p} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filterDrop.includes(p)}
                        onChange={() =>
                          setFilterDrop((prev) =>
                            prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
                          )
                        }
                      />
                      <span className="custom-check" />
                      <Navigation size={11} style={{ flexShrink: 0, color: "var(--rose)" }} />
                      {p}
                    </label>
                  ))}
              </div>
              {dropPoints.length > 4 && (
                <button className="clear-all-btn" onClick={() => setShowAllDrop((s) => !s)}>
                  {showAllDrop ? "Show less" : `Show all (${dropPoints.length})`}
                </button>
              )}
            </div>
          )}

          {dropTimeBuckets.size > 0 && (
            <div className="filter-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="filter-label">Drop time - {to.split(",")[0]}</h4>
                {filterDropTime.length > 0 && (
                  <button className="clear-all-btn" onClick={() => setFilterDropTime([])}>CLEAR</button>
                )}
              </div>
              <div className="checkbox-list">
                {[
                  { value: "before6", label: "12 AM - 6 AM" },
                  { value: "6to12", label: "6 AM - 12 PM" },
                  { value: "12to18", label: "12 PM - 6 PM" },
                  { value: "after18", label: "6 PM - 12 AM" },
                ].filter(({ value }) => dropTimeBuckets.has(value))
                  .map(({ value, label }) => (
                    <label key={value} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={filterDropTime.includes(value)}
                        onChange={() =>
                          setFilterDropTime((prev) =>
                            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
                          )
                        }
                      />
                      <span className="custom-check" />
                      {label}
                    </label>
                  ))}
              </div>
            </div>
          )}

          <div className="filter-group">
            <h4 className="filter-label">
              Max Price <span className="price-val">₹{filterPriceMax.toLocaleString()}</span>
            </h4>
            <input
              type="range"
              min={0}
              max={maxPrice}
              step={100}
              value={filterPriceMax}
              onChange={(e) => setFilterPriceMax(Number(e.target.value))}
              className="price-slider"
            />
            <div className="price-range-labels">
              <span>₹0</span>
              <span>₹{maxPrice.toLocaleString()}</span>
            </div>
          </div>

          {hasAnyRating && (
            <div className="filter-group">
              <h4 className="filter-label">Minimum Rating</h4>
              <div className="rating-pills">
                {[0, 3, 3.5, 4, 4.5].map((r) => (
                  <button
                    key={r}
                    className={`rating-pill ${filterRating === r ? "rp-active" : ""}`}
                    onClick={() => setFilterRating(r)}
                  >
                    {r === 0 ? "All" : `${r}+ ★`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasAnyGPS && (
            <div className="filter-group">
              <label className="toggle-row">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Satellite size={13} /> GPS Enabled Only
                </span>
                <input
                  type="checkbox"
                  className="toggle-input"
                  checked={filterGPS}
                  onChange={(e) => setFilterGPS(e.target.checked)}
                />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
              </label>
            </div>
          )}

          {hasAnyAmenities && (
            <CheckboxGroup
              label="Amenities"
              options={Object.entries(amenityIcons).map(([k, v]) => ({
                value: k,
                label: <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>{v.icon} {v.label}</span>
              }))}
              selected={filterAmenities}
              onChange={setFilterAmenities}
            />
          )}

        </aside>

        {/* ── Main Results ── */}
        <main className="bp-main">
          <div className="results-header">
            <div className="results-meta">
              <button className="filter-toggle-btn" onClick={() => setFiltersOpen(!filtersOpen)}>
                <SlidersHorizontal size={13} style={{ marginRight: 4 }} />
                {filtersOpen ? "Hide Filters" : "Filters"}
              </button>
              <span className="results-count">
                <strong>{filtered.length}</strong> buses
                <span className="route-label">· {from.split(",")[0]} → {to.split(",")[0]}</span>
              </span>
            </div>
            <div className="sort-bar">
              <span className="sort-label">Sort:</span>
              {sortOptions.map((s) => (
                <button
                  key={s.value}
                  className={`sort-btn ${sortBy === s.value ? "sort-active" : ""}`}
                  onClick={() => setSortBy(s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {defaultLoading || searchLoading ? (
            <div className="no-results">
              <Bus className="no-icon" size={42} />
              <h3>Loading buses...</h3>
              <p>Please wait while we fetch available buses.</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="no-results">
              <Bus className="no-icon" size={42} />
              <h3>No buses found</h3>
              <p>Try adjusting your filters or search for a different route.</p>
              <button className="clear-all-btn big" onClick={clearAll}>Clear All Filters</button>
            </div>
          ) : (
            <div className="bus-list">
              {paginatedBuses.map((bus) => (
                <div key={bus.id} className={`bus-card ${expandedId === bus.id ? "expanded" : ""}`}>
                  <div className="bus-card-main">
                    {(bus.tags.length > 0 || bus.hasGPS) && (
                      <div className="bus-tags">
                        {bus.tags.includes("top-rated") && (
                          <span className="tag tag-gold">
                            <Trophy size={9} /> Top Rated
                          </span>
                        )}
                        {bus.tags.includes("luxury") && (
                          <span className="tag tag-purple">
                            <Crown size={9} /> Luxury
                          </span>
                        )}
                        {bus.tags.includes("safety") && (
                          <span className="tag tag-blue">
                            <Shield size={9} /> Safety
                          </span>
                        )}
                        {bus.tags.includes("budget") && (
                          <span className="tag tag-green">
                            <Wallet size={9} /> Budget
                          </span>
                        )}
                        {bus.tags.includes("fastest") && (
                          <span className="tag tag-orange">
                            <Zap size={9} /> Fastest
                          </span>
                        )}
                        {bus.hasGPS && (
                          <span className="tag tag-gray">
                            <Satellite size={9} /> GPS
                          </span>
                        )}
                      </div>
                    )}


                    <CompactBusCard bus={bus} onSelect={setBookingBus} />
                    <AmenityChips amenities={bus.amenities} />

                    <div className="card-actions-bar">
                      <button className="expand-btn" onClick={() => toggleExpand(bus.id)}>
                        {expandedId === bus.id
                          ? <><ChevronUp size={13} /> Hide Details</>
                          : <><ChevronDown size={13} /> More Details</>}
                      </button>

                      <label
                        className={`bcc-compare-label ${compareList.some((b) => b.id === bus.id) ? "active" : ""} ${!compareList.some((b) => b.id === bus.id) && compareList.length >= 3 ? "disabled" : ""
                          }`}
                        title={
                          !compareList.some((b) => b.id === bus.id) && compareList.length >= 3
                            ? "You can compare up to 3 buses at a time"
                            : ""
                        }
                      >
                        <input
                          type="checkbox"
                          checked={compareList.some((b) => b.id === bus.id)}
                          disabled={!compareList.some((b) => b.id === bus.id) && compareList.length >= 3}
                          onChange={() => toggleCompare(bus)}
                        />
                        <span className="bcc-compare-check" />
                        Compare
                      </label>
                    </div>
                  </div>

                  {expandedId === bus.id && (
                    <div className="bus-details">
                      <div className="detail-tabs">
                        {[
                          { key: "boarding", icon: <MapPin size={15} />, label: "Boarding & Drop" },
                          { key: "photos", icon: <Image size={15} />, label: "Photos" },
                          { key: "policies", icon: <FileText size={15} />, label: "Policies" },
                          { key: "reviews", icon: <Star size={15} />, label: "Reviews" },
                        ].map((t) => (
                          <button
                            key={t.key}
                            className={`detail-tab ${expandedTab === t.key ? "dtab-active" : ""}`}
                            onClick={() => setExpandedTab(t.key)}
                          >
                            {t.icon} {t.label}
                          </button>
                        ))}
                      </div>
                      <div className="detail-body">
                        {expandedTab === "boarding" && (
                          <div className="detail-points">
                            <div className="point-col">
                              <h5><MapPin size={16} color="#16a34a" /> Boarding Points</h5>
                              <PointRows points={bus.boardingPoints} dotClass="green" fallbackTime={bus.departure} />
                            </div>
                            <div className="point-col">
                              <h5><MapPin size={16} color="#dc2626" /> Drop Points</h5>
                              <PointRows points={bus.droppingPoints} dotClass="red" fallbackTime={bus.arrival} />
                            </div>
                          </div>
                        )}
                        {expandedTab === "photos" && (
                          <div className="photos-grid">
                            {bus.photos.length ? (
                              bus.photos.map((photo, index) => (
                                <figure key={`${photo.url}-${index}`} className="bus-photo">
                                  <img src={photo.url} alt={photo.title || `${bus.operator} bus photo ${index + 1}`} />
                                  {photo.title && <figcaption>{photo.title}</figcaption>}
                                </figure>
                              ))
                            ) : (
                              <DetailEmpty>No photos returned by the API.</DetailEmpty>
                            )}
                          </div>
                        )}
                        {expandedTab === "policies" && (
                          <div className="policies-list">
                            {bus.policies.length ? (
                              bus.policies.map((policy, index) => (
                                <div key={`${policy.title}-${index}`} className="policy-item">
                                  {policy.icon && <span className="policy-icon">{policy.icon}</span>}
                                  <div>
                                    <strong>{policy.title}</strong>
                                    {policy.text && <p>{policy.text}</p>}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <DetailEmpty>No policies returned by the API.</DetailEmpty>
                            )}
                          </div>
                        )}
                        {expandedTab === "reviews" && (
                          <div className="reviews-list">
                            <div className="rating-overview">
                              <div className="big-rating">{bus.rating.toFixed(1)}</div>
                              <div>
                                <StarRating rating={bus.rating} />
                                <div className="rating-total">{bus.totalRatings} reviews</div>
                              </div>
                            </div>
                            {bus.reviewsList.length ? (
                              bus.reviewsList.map((r, i) => (
                                <div key={`${r.name}-${i}`} className="review-item">
                                  <div className="reviewer">
                                    <div className="reviewer-avatar">{(r.name || "T").charAt(0)}</div>
                                    <div>
                                      <div className="reviewer-name">{r.name || "Traveller"}</div>
                                      {r.stars > 0 && <StarRating rating={r.stars} />}
                                      {r.date && <div className="rating-total">{r.date}</div>}
                                    </div>
                                  </div>
                                  {r.comment && <p className="review-text">{r.comment}</p>}
                                </div>
                              ))
                            ) : (
                              <DetailEmpty>No reviews returned by the API.</DetailEmpty>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                            ))}

              {filtered.length > BUSES_PER_PAGE && (
                <div className="bus-pagination">
                  <div className="bus-pagination-summary">
                    Showing {visibleBusStart}–{visibleBusEnd} of{" "}
                    {filtered.length} buses
                  </div>

                  <div className="bus-pagination-controls">
                    <button
                      type="button"
                      className="bus-pagination-btn bus-pagination-nav"
                      onClick={() =>
                        goToBusPage(safeCurrentPage - 1)
                      }
                      disabled={safeCurrentPage === 1}
                    >
                      Previous
                    </button>

                    <div className="bus-pagination-pages">
                      {Array.from(
                        { length: totalPages },
                        (_, index) => index + 1
                      ).map((page) => (
                        <button
                          key={page}
                          type="button"
                          className={`bus-pagination-btn ${
                            safeCurrentPage === page
                              ? "active"
                              : ""
                          }`}
                          onClick={() => goToBusPage(page)}
                        >
                          {page}
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      className="bus-pagination-btn bus-pagination-nav"
                      onClick={() =>
                        goToBusPage(safeCurrentPage + 1)
                      }
                      disabled={
                        safeCurrentPage === totalPages
                      }
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
      <BusCompareTray
        selectedBuses={compareList}
        onOpen={() => setCompareOpen(true)}
        onRemove={removeCompare}
      />

      {compareOpen && (
        <BusCompare
          buses={compareList}
          onClose={() => setCompareOpen(false)}
          onRemove={removeCompare}
          onBook={(bus) => { setCompareOpen(false); setBookingBus(bus); }}
        />
      )}
    </div>
  );
}